import * as React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { createColumnHelper } from "@tanstack/react-table";

import { resolveInject } from "app/di";
import {
    ISparqlExecutor,
    SparqlQueryParameters
} from "app/services/infrastructure/sparql-executor";
import { IData, OverviewTable } from "./overview-table";
import { SparqlRecord, SparqlResultParser } from "app/services/infrastructure/sparql-result-parser";
import { Box } from "@mui/material";

interface IComponentProps {
    query: string;
    queryParameters: SparqlQueryParameters;
}

export const OverviewTableWrapper: React.FC<IComponentProps> = (props: IComponentProps) => {
    const sparqlExecutor = resolveInject(ISparqlExecutor);
    const columnHelper = createColumnHelper<IData>();

    const [propsMemo, setPropsMemo] = React.useState(new String());
    const [data, setData] = React.useState<IData[]>([]);
    const [columns, setColumns] = React.useState<any[]>([]);
    const [isLoading, setLoading] = React.useState(false);

    const updateColumns = (headers: string[]) => {
        const columns = headers.map((header) =>
            columnHelper.accessor((row) => row[header], {
                id: header,
                cell: (info) => info.getValue(),
                header: () => <span>{header}</span>,
                footer: (info) => info.column.id
            })
        );
        setColumns(columns);
    };

    const updateData = (records: SparqlRecord[], headers: string[]) => {
        const tableData: IData[] = [];
        for (let record of records) {
            const item: IData = {};

            for (let header of headers) {
                item[header] = record[header]?.value || "";
            }
            tableData.push(item);
        }
        setData(tableData);
    };

    React.useEffect(() => {
        const fetchResults = async () => {
            const currPropsConfig = props.query + JSON.stringify(props.queryParameters);
            if (propsMemo != currPropsConfig) {
                setLoading(true);
                const result = await sparqlExecutor.execute(props.query, props.queryParameters);
                const headers = result.head.vars;
                const records = SparqlResultParser.parse(result);

                updateColumns(headers);
                updateData(records, headers);
                setLoading(false);
                setPropsMemo(currPropsConfig);
            }
        };

        fetchResults();
    }, [props.query, props.queryParameters]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={500}>
                <CircularProgress size={48} color="primary" />
            </Box>
        );
    }

    return <OverviewTable data={data} columns={columns} />;
};
